/**
 * Sync Queue Service
 * 
 * Manages a queue of actions that need to be synced when the device comes online.
 * Actions are stored persistently and executed in order when network is available.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { networkMonitor } from './networkMonitor';

const SYNC_QUEUE_KEY = '@tripflow_sync_queue';
const SYNC_DEAD_LETTER_KEY = '@tripflow_sync_dead_letter_queue';
const MAX_RETRIES = 3;
/** More attempts for trip data that often hits transient Firestore/network errors. */
const EXTENDED_MAX_RETRIES = 6;
const MAX_QUEUE_SIZE = 1000; // Prevent queue from growing too large

export type SyncActionType = 
  | 'create_expense'
  | 'update_expense'
  | 'delete_expense'
  | 'create_booking'
  | 'update_booking'
  | 'delete_booking'
  | 'update_trip'
  | 'create_itinerary_item'
  | 'update_itinerary_item'
  | 'delete_itinerary_item'
  | 'create_comment'
  | 'update_comment'
  | 'delete_comment'
  | 'update_maybe_item'
  | 'archive_maybe_item'
  | 'upload_document'
  | 'save_manual_document';

export type SyncAction = {
  id: string;
  type: SyncActionType;
  payload: Record<string, unknown>;
  timestamp: number;
  retries: number;
  tripId?: string;
  userId?: string;
};

export type DeadLetterSyncAction = SyncAction & {
  failedAt: number;
  reason: string;
};

const EXTENDED_RETRY_ACTION_TYPES: ReadonlySet<SyncActionType> = new Set([
  'upload_document',
  'save_manual_document',
  'create_expense',
  'update_expense',
  'delete_expense',
  'create_itinerary_item',
  'update_itinerary_item',
  'delete_itinerary_item',
  'create_booking',
  'update_booking',
  'delete_booking',
  'update_trip',
  'update_maybe_item',
  'archive_maybe_item',
]);

type SyncQueue = {
  actions: SyncAction[];
  lastProcessed: number;
};

type SyncHandler = (action: SyncAction) => Promise<void>;

class SyncQueueManager {
  private handlers: Map<SyncActionType, SyncHandler> = new Map();
  private isProcessing = false;
  private processingPromise: Promise<void> | null = null;
  private processingListeners: Set<(isProcessing: boolean) => void> = new Set();
  private queueMutatedListeners: Set<() => void> = new Set();
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Register a handler for a specific action type
   */
  registerHandler(actionType: SyncActionType, handler: SyncHandler): void {
    this.handlers.set(actionType, handler);
  }

  /**
   * Add an action to the sync queue
   */
  async queueAction(action: Omit<SyncAction, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    try {
      const queue = await this.getQueue();
      
      // Prevent queue from growing too large
      if (queue.actions.length >= MAX_QUEUE_SIZE) {
        console.warn('[SyncQueue] Queue is full, removing oldest actions');
        queue.actions = queue.actions.slice(-MAX_QUEUE_SIZE + 1);
      }

      const syncAction: SyncAction = {
        ...action,
        id: `${action.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        retries: 0,
      };

      queue.actions.push(syncAction);
      await this.saveQueue(queue);
      this.notifyQueueMutated();

      console.log(`[SyncQueue] Queued action: ${syncAction.type} (${queue.actions.length} in queue)`);
      
      // Try to process if online
      if (networkMonitor.isOnline()) {
        this.processQueue().catch(err => {
          console.error('[SyncQueue] Error processing queue after adding action:', err);
        });
      }
    } catch (error) {
      console.error('[SyncQueue] Error queueing action:', error);
      throw error;
    }
  }

  /**
   * Get all queued actions
   */
  async getQueuedActions(): Promise<SyncAction[]> {
    const queue = await this.getQueue();
    return [...queue.actions];
  }

  /**
   * Get queue size
   */
  async getQueueSize(): Promise<number> {
    const queue = await this.getQueue();
    return queue.actions.length;
  }

  /**
   * Get actions that permanently failed and were removed from queue
   */
  async getDeadLetterActions(): Promise<DeadLetterSyncAction[]> {
    return this.getDeadLetterQueue();
  }

  /**
   * Process the sync queue (called when online)
   */
  async processQueue(): Promise<void> {
    if (this.isProcessing) {
      console.log('[SyncQueue] Already processing queue');
      return this.processingPromise ?? Promise.resolve();
    }

    if (!networkMonitor.isOnline()) {
      console.log('[SyncQueue] Device is offline, skipping queue processing');
      return;
    }

    this.setProcessing(true);
    this.processingPromise = (async () => {
      try {
        const queue = await this.getQueue();
        
        if (queue.actions.length === 0) {
          this.notifyQueueMutated();
          return;
        }

        console.log(`[SyncQueue] Processing ${queue.actions.length} queued actions`);

        const actionsToProcess = [...queue.actions];
        const processed: string[] = [];
        const failed: SyncAction[] = [];

        // Group similar actions for batching (e.g., multiple expense updates)
        const actionGroups = this.groupActionsForBatching(actionsToProcess);

        for (const group of actionGroups) {
          // If group has multiple actions of same type, try to batch them
          if (group.length > 1 && this.canBatchActions(group)) {
            try {
              await this.processBatch(group);
              group.forEach(action => processed.push(action.id));
              console.log(`[SyncQueue] Successfully processed batch of ${group.length} ${group[0].type} actions`);
            } catch (error) {
              console.error(`[SyncQueue] Error processing batch:`, error);
              // Fall back to individual processing
              for (const action of group) {
                try {
                  await this.processAction(action);
                  processed.push(action.id);
                } catch (err) {
                  await this.handleActionError(action, err, failed);
                }
              }
            }
          } else {
            // Process individually
            for (const action of group) {
              try {
                await this.processAction(action);
                processed.push(action.id);
              } catch (error) {
                await this.handleActionError(action, error, failed);
              }
            }
          }
        }

        // Remove processed actions and update failed ones
        queue.actions = failed;
        queue.lastProcessed = Date.now();
        await this.saveQueue(queue);
        this.notifyQueueMutated();

        // Keep draining queued retries without requiring repeated "Sync Now" taps.
        if (failed.length > 0 && networkMonitor.isOnline()) {
          const retryDelay = this.getRetryDelayForFailedActions(failed);
          this.scheduleRetry(retryDelay);
        } else {
          this.clearRetryTimer();
        }

        console.log(`[SyncQueue] Processed ${processed.length} actions, ${failed.length} failed/retrying`);
      } catch (error) {
        console.error('[SyncQueue] Error processing queue:', error);
      } finally {
        this.setProcessing(false);
        this.processingPromise = null;
      }
    })();

    return this.processingPromise;
  }

  /**
   * Clear the sync queue
   */
  async clearQueue(): Promise<void> {
    this.clearRetryTimer();
    const emptyQueue: SyncQueue = {
      actions: [],
      lastProcessed: Date.now(),
    };
    await this.saveQueue(emptyQueue);
    this.notifyQueueMutated();
  }

  /**
   * Clear dead-letter queue (use with caution)
   */
  async clearDeadLetterQueue(): Promise<void> {
    try {
      await AsyncStorage.removeItem(SYNC_DEAD_LETTER_KEY);
      this.notifyQueueMutated();
    } catch (error) {
      console.error('[SyncQueue] Error clearing dead-letter queue:', error);
      throw error;
    }
  }

  /**
   * Retry one dead-letter action by moving it back to the active queue.
   */
  async retryDeadLetterAction(actionId: string): Promise<boolean> {
    const dead = await this.getDeadLetterQueue();
    const match = dead.find((a) => a.id === actionId);
    if (!match) return false;

    const remaining = dead.filter((a) => a.id !== actionId);
    await this.saveDeadLetterQueue(remaining);

    await this.queueAction({
      type: match.type,
      payload: match.payload,
      tripId: match.tripId,
      userId: match.userId,
    });
    this.notifyQueueMutated();
    return true;
  }

  /**
   * Retry all dead-letter actions by moving them back to the active queue.
   */
  async retryAllDeadLetterActions(): Promise<number> {
    const dead = await this.getDeadLetterQueue();
    if (dead.length === 0) return 0;

    await this.saveDeadLetterQueue([]);
    let moved = 0;
    for (const action of dead) {
      await this.queueAction({
        type: action.type,
        payload: action.payload,
        tripId: action.tripId,
        userId: action.userId,
      });
      moved += 1;
    }
    this.notifyQueueMutated();
    return moved;
  }

  /**
   * Remove a specific action from the queue
   */
  async removeAction(actionId: string): Promise<void> {
    const queue = await this.getQueue();
    queue.actions = queue.actions.filter(action => action.id !== actionId);
    await this.saveQueue(queue);
    this.notifyQueueMutated();
  }

  /**
   * Remove queued actions matching a predicate (e.g. cancel a pending create when user deletes offline-first).
   * Returns how many actions were removed.
   */
  async removeActionsWhere(predicate: (action: SyncAction) => boolean): Promise<number> {
    const queue = await this.getQueue();
    const before = queue.actions.length;
    queue.actions = queue.actions.filter((action) => !predicate(action));
    const removed = before - queue.actions.length;
    if (removed > 0) {
      await this.saveQueue(queue);
      this.notifyQueueMutated();
    }
    return removed;
  }

  /**
   * Subscribe to processing status changes
   */
  onProcessingChange(listener: (isProcessing: boolean) => void): () => void {
    this.processingListeners.add(listener);
    return () => {
      this.processingListeners.delete(listener);
    };
  }

  /**
   * Fires after the persisted queue changes (enqueue, dequeue, clear).
   */
  subscribeToQueueChanges(listener: () => void): () => void {
    this.queueMutatedListeners.add(listener);
    return () => {
      this.queueMutatedListeners.delete(listener);
    };
  }

  private notifyQueueMutated(): void {
    this.queueMutatedListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('[SyncQueue] Error in queue change listener:', error);
      }
    });
  }

  /**
   * Get processing status
   */
  isProcessingQueue(): boolean {
    return this.isProcessing;
  }

  /**
   * Get queue from storage
   */
  private async getQueue(): Promise<SyncQueue> {
    try {
      const stored = await AsyncStorage.getItem(SYNC_QUEUE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('[SyncQueue] Error reading queue:', error);
    }

    return {
      actions: [],
      lastProcessed: 0,
    };
  }

  /**
   * Save queue to storage
   */
  private async saveQueue(queue: SyncQueue): Promise<void> {
    try {
      await AsyncStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('[SyncQueue] Error saving queue:', error);
      throw error;
    }
  }

  /**
   * Get dead-letter queue from storage
   */
  private async getDeadLetterQueue(): Promise<DeadLetterSyncAction[]> {
    try {
      const stored = await AsyncStorage.getItem(SYNC_DEAD_LETTER_KEY);
      if (stored) {
        return JSON.parse(stored) as DeadLetterSyncAction[];
      }
    } catch (error) {
      console.error('[SyncQueue] Error reading dead-letter queue:', error);
    }

    return [];
  }

  /**
   * Save dead-letter queue to storage
   */
  private async saveDeadLetterQueue(actions: DeadLetterSyncAction[]): Promise<void> {
    try {
      await AsyncStorage.setItem(SYNC_DEAD_LETTER_KEY, JSON.stringify(actions));
    } catch (error) {
      console.error('[SyncQueue] Error saving dead-letter queue:', error);
      throw error;
    }
  }

  /**
   * Move a failed action to dead-letter queue for support/debug visibility.
   */
  private async moveToDeadLetter(action: SyncAction, error: unknown): Promise<void> {
    const deadLetter = await this.getDeadLetterQueue();
    const reason = error instanceof Error ? error.message : String(error);
    deadLetter.push({
      ...action,
      failedAt: Date.now(),
      reason,
    });
    await this.saveDeadLetterQueue(deadLetter);
    this.notifyQueueMutated();
  }

  /**
   * Set processing status and notify listeners
   */
  private setProcessing(isProcessing: boolean): void {
    this.isProcessing = isProcessing;
    this.processingListeners.forEach(listener => {
      try {
        listener(isProcessing);
      } catch (error) {
        console.error('[SyncQueue] Error in processing listener:', error);
      }
    });
  }

  /**
   * Group actions for potential batching
   */
  private groupActionsForBatching(actions: SyncAction[]): SyncAction[][] {
    const groups: Map<string, SyncAction[]> = new Map();
    
    for (const action of actions) {
      // Group by type and tripId for batching
      const key = `${action.type}_${action.tripId || 'global'}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(action);
    }
    
    return Array.from(groups.values());
  }

  /**
   * Check if actions can be batched
   */
  private canBatchActions(actions: SyncAction[]): boolean {
    if (actions.length < 2) return false;
    
    const firstType = actions[0].type;
    // Only batch certain action types
    const batchableTypes: SyncActionType[] = [
      'create_expense',
      'update_expense',
      'create_booking',
      'update_booking',
    ];
    
    return batchableTypes.includes(firstType) && 
           actions.every(a => a.type === firstType);
  }

  /**
   * Process a batch of actions
   */
  private async processBatch(actions: SyncAction[]): Promise<void> {
    const handler = this.handlers.get(actions[0].type);
    if (!handler) {
      throw new Error(`No handler for ${actions[0].type}`);
    }
    
    // Process batch sequentially to maintain order
    for (const action of actions) {
      await handler(action);
    }
  }

  /**
   * Process a single action
   */
  private async processAction(action: SyncAction): Promise<void> {
    const handler = this.handlers.get(action.type);
    
    if (!handler) {
      throw new Error(
        `[SyncQueue] No handler registered for action type: ${action.type} — ensure syncQueueBootstrap loads before processQueue`,
      );
    }

    await handler(action);
    console.log(`[SyncQueue] Successfully processed action: ${action.type} (${action.id})`);
  }

  /**
   * Handle action error with retry logic
   */
  private async handleActionError(
    action: SyncAction,
    error: unknown,
    failed: SyncAction[]
  ): Promise<void> {
    console.error(`[SyncQueue] Error processing action ${action.id}:`, error);

    if (this.isMissingHandlerError(error)) {
      action.retries = MAX_RETRIES;
      console.error(
        `[SyncQueue] Action ${action.id} has unsupported type ${action.type}, moving to dead-letter queue immediately`,
      );
      await this.moveToDeadLetter(action, error);
      return;
    }
    
    action.retries += 1;

    const maxRetriesForAction = EXTENDED_RETRY_ACTION_TYPES.has(action.type)
      ? EXTENDED_MAX_RETRIES
      : MAX_RETRIES;

    if (action.retries >= maxRetriesForAction) {
      console.error(`[SyncQueue] Action ${action.id} exceeded max retries, removing from queue`);
      await this.moveToDeadLetter(action, error);
    } else {
      // Exponential backoff: wait longer before retrying
      const backoffDelay = Math.min(1000 * Math.pow(2, action.retries), 30000);
      console.log(`[SyncQueue] Action ${action.id} will retry after ${backoffDelay}ms`);
      failed.push(action);
    }
  }

  private getRetryDelayForFailedActions(failed: SyncAction[]): number {
    if (failed.length === 0) return 0;
    let minDelay = 30_000;
    for (const action of failed) {
      const delay = Math.min(1000 * Math.pow(2, action.retries), 30000);
      if (delay < minDelay) minDelay = delay;
    }
    // Keep retries responsive while still backing off.
    return Math.max(500, minDelay);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private scheduleRetry(delayMs: number): void {
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!networkMonitor.isOnline()) return;
      void this.processQueue().catch((error) => {
        console.error('[SyncQueue] Auto-retry processing failed:', error);
      });
    }, delayMs);
  }

  private isMissingHandlerError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return (
      error.message.includes('No handler registered for action type') ||
      error.message.includes('No handler for ')
    );
  }
}

// Singleton instance
export const syncQueue = new SyncQueueManager();

